import { tokens } from '../utils/design-tokens';

type EventCardSkeletonProps = {
  delay?: number;
};

export function EventCardSkeleton({ delay = 0 }: EventCardSkeletonProps) {
  return (
    <div 
      className="flex items-center justify-center relative w-full"
      style={{ 
        borderRadius: tokens.radius('sm'),
        boxShadow: tokens.shadow('tracker'),
        backgroundColor: `var(--color-bg-secondary)`,
      }}
    >
      <div className="flex flex-row items-center self-stretch w-full min-w-0">
        {/* Main content skeleton */}
        <div 
          className="basis-0 flex-auto min-h-px min-w-0 relative shrink-0 w-full"
          style={{ 
            borderRadius: tokens.radius('sm')
          }}
        >
          <div 
            className="box-border content-stretch flex flex-col items-center justify-center overflow-visible relative w-full p-2 xs:p-3 sm:p-4"
            style={{ 
              gap: tokens.space('lg'),
            }}
          >
            {/* Top section skeleton */}
            <div className="content-stretch flex items-center justify-between relative shrink-0 w-full min-w-0 gap-0.5 xxs:gap-1 xs:gap-2">
              <div className="basis-0 flex flex-row grow items-center self-stretch shrink-0 min-w-0">
                <div 
                  className="basis-0 content-stretch flex grow h-full items-start justify-start min-h-px min-w-0 relative shrink-0"
                  style={{ gap: tokens.space('lg') }}
                >
                  {/* Timer skeleton */}
                  <div className="content-stretch flex flex-col items-end relative shrink-0">
                    <div className="skeleton-text skeleton-timer" />
                    <div className="skeleton-text skeleton-countdown" />
                  </div>
                  
                  {/* Title and Location skeleton */}
                  <div 
                    className="basis-0 content-stretch flex flex-col grow h-full items-start justify-top leading-[0] max-w-[240px] xxs:max-w-[140px] xs:max-w-[160px] sm:max-w-[200px] md:max-w-[230px] lg:max-w-[280px] xl:max-w-[305px] min-h-px min-w-0 text-pretty relative gap-0.5 shrink-0 mr-1 sm:mr-2"
                  >
                    <div className="skeleton-text skeleton-title" />
                    <div className="skeleton-text skeleton-location" />
                  </div>
                </div>
              </div>
              
              {/* Right side - Controls skeleton */}
              <div className="flex flex-row items-center self-stretch shrink-0">
                <div 
                  className="content-stretch flex flex-col sm:flex-row h-full items-start justify-start relative shrink-0"
                  style={{ gap: tokens.space('sm') }}
                >
                  {/* Three button group skeleton */}
                  <div 
                    className="flex flex-row items-start justify-start relative shrink-0"
                    style={{ gap: tokens.space('sm') }}
                  >
                    <div className="skeleton-button" />
                    <div className="skeleton-button" />
                    <div className="skeleton-button" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bottom section - Progress bar skeleton */}
            <div className="content-stretch flex items-center justify-between relative shrink-0 w-full min-w-0 gap-2">
              <div className="skeleton-progress-bar" />
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .skeleton-text {
          background: linear-gradient(
            90deg,
            var(--color-bg-tertiary) 0%,
            rgba(0, 0, 0, 0.05) 40%,
            rgba(0, 0, 0, 0.08) 50%,
            rgba(0, 0, 0, 0.05) 60%,
            var(--color-bg-tertiary) 100%
          );
          background-size: 300% 100%;
          animation: skeleton-wave 3s ease-in-out infinite;
          animation-delay: ${delay}ms;
          border-radius: ${tokens.radius('xs')};
          height: 1em;
        }
        
        .skeleton-timer {
          width: 60px;
          height: 20px;
          margin-bottom: 4px;
        }
        
        .skeleton-countdown {
          width: 40px;
          height: 14px;
        }
        
        .skeleton-title {
          width: 100%;
          max-width: 200px;
          height: 22px;
          margin-bottom: 6px;
        }
        
        .skeleton-location {
          width: 80%;
          max-width: 150px;
          height: 16px;
        }
        
        .skeleton-button {
          width: 32px;
          height: 32px;
          background: linear-gradient(
            90deg,
            var(--color-bg-tertiary) 0%,
            rgba(0, 0, 0, 0.05) 40%,
            rgba(0, 0, 0, 0.08) 50%,
            rgba(0, 0, 0, 0.05) 60%,
            var(--color-bg-tertiary) 100%
          );
          background-size: 300% 100%;
          animation: skeleton-wave 3s ease-in-out infinite;
          animation-delay: ${delay}ms;
          border-radius: ${tokens.radius('sm')};
        }
        
        @media (min-width: 480px) {
          .skeleton-button {
            width: 40px;
            height: 40px;
          }
        }
        
        .skeleton-progress-bar {
          width: 100%;
          height: 8px;
          background: linear-gradient(
            90deg,
            var(--color-bg-tertiary) 0%,
            rgba(0, 0, 0, 0.05) 40%,
            rgba(0, 0, 0, 0.08) 50%,
            rgba(0, 0, 0, 0.05) 60%,
            var(--color-bg-tertiary) 100%
          );
          background-size: 300% 100%;
          animation: skeleton-wave 3s ease-in-out infinite;
          animation-delay: ${delay}ms;
          border-radius: ${tokens.radius('xs')};
        }
        
        @keyframes skeleton-wave {
          0% {
            background-position: 300% 0;
          }
          100% {
            background-position: 0% 0;
          }
        }
      `}</style>
    </div>
  );
}

